import {get, post} from './http';

// 'qc' = rejected by QC review before delivery; 'qa' = judged a false positive by Weekly QA after it.
export type RejectionSource = 'qc' | 'qa';

export type RejectedSubmission = {
  source: RejectionSource;
  id: string;
  submission_id: string;
  policy_violation: string;
  original_policy_violation: string;
  original_risk: string;
  // QC only - the analyst's own notes on the held submission.
  original_notes: string | null;
  corrected_policy_violation: string | null;
  corrected_risk: string | null;
  // QA only - the reviewer judged the item not a risk at all.
  no_risk: boolean;
  reviewer_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string;
  item_external_id: string | null;
  title: string | null;
  url: string | null;
  thumbnail_url: string | null;
  current_delivery_state: string | null;
};

export type RejectedSubmissionList = {
  results: RejectedSubmission[];
  page: number;
  page_size: number;
  total_count: number;
};

export type RejectedSubmissionsQuery = {
  source?: RejectionSource[];
  policy_violation?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
  page?: number;
  page_size?: number;
};

export type RejectedSubmissionCount = {policy_violation: string; count: number};

export type RejectedSubmissionCountsQuery = Omit<
  RejectedSubmissionsQuery,
  'policy_violation' | 'page' | 'page_size'
>;

export type ReviewFeedbackTheme = {
  title: string;
  guidance: string;
  source: RejectionSource | 'both';
};

export type ReviewFeedbackSummary = {
  policy_violation: string;
  summary: {summary: string; themes: ReviewFeedbackTheme[]} | null;
  qc_count: number;
  qa_count: number;
  generated_at: string;
};

export const rejectedSubmissionsApi = {
  // Scoped server-side to the caller's own rows.
  list: async (parameters?: RejectedSubmissionsQuery) =>
    get<RejectedSubmissionList>('/rejected-submissions', parameters),

  // Per-vertical totals under the same filters, minus the vertical itself.
  counts: async (parameters?: RejectedSubmissionCountsQuery) =>
    get<RejectedSubmissionCount[]>('/rejected-submissions/counts', parameters),

  // One per vertical the caller may see; verticals with too little feedback are simply absent.
  summaries: async () => get<ReviewFeedbackSummary[]>('/rejected-submissions/summaries'),

  // The caller's own QC rejections not yet dismissed from the Dashboard (the library ignores dismissal).
  qcFeedback: async () => get<RejectedSubmission[]>('/rejected-submissions/qc-feedback'),

  acknowledgeQcFeedback: async (id: string) =>
    post<undefined>(`/rejected-submissions/qc-feedback/${id}/acknowledge`)
};
